import React from "react";
import { BigNumber } from "bignumber.js";
import { getCurrencySymbol } from "common/currency";
import * as styles from "./AmountPresets.module.scss";

type AmountPresetsProps = {
  currency: string;
  /** current field value, in whole currency units, that increments add to */
  value: number;
  onSelect: (amount: number) => void;
  /** offered before the last preset, in whole currency units */
  increments?: readonly number[];
  /** ceiling the Max preset selects, in cents */
  maxAmountInCents?: number;
  /**
   * Takes Max's place as a `Req` preset, in whole currency units. What the
   * caller needs funded is the more useful of the two targets, and the row has
   * no room for both. Omit to keep Max.
   */
  requiredAmount?: number;
  disabled?: boolean;
};

export const AmountPresets = ({
  currency,
  value,
  onSelect,
  increments = [],
  maxAmountInCents,
  requiredAmount,
  disabled = false,
}: AmountPresetsProps) => {
  const maxAmount = BigNumber(maxAmountInCents ?? 0)
    .dividedBy(100)
    .decimalPlaces(2)
    .toNumber();

  // a zero ceiling means the account has no headroom to deposit into at all,
  // so nothing in the row can produce a valid amount
  const noHeadroom = maxAmount === 0;
  const atMax = noHeadroom || maxAmount === value;

  const showRequired = requiredAmount !== undefined;

  return (
    <div className={styles.amountPresets}>
      {increments.map((increment) => (
        <button
          key={`amount-preset-${increment}`}
          type="button"
          className={styles.presetButton}
          disabled={disabled || atMax}
          onClick={() =>
            onSelect(
              BigNumber(value).plus(increment).decimalPlaces(2).toNumber(),
            )
          }
        >
          <span>
            +{getCurrencySymbol(currency)}
            {increment}
          </span>
        </button>
      ))}

      {showRequired ? (
        <button
          key="amount-preset-required"
          type="button"
          className={styles.presetButton}
          onClick={() => onSelect(requiredAmount)}
          disabled={disabled || noHeadroom || requiredAmount === value}
        >
          <span>Req</span>
        </button>
      ) : (
        <button
          key="amount-preset-max"
          type="button"
          className={styles.presetButton}
          onClick={() => onSelect(maxAmount)}
          disabled={disabled || atMax}
        >
          <span>Max</span>
        </button>
      )}
    </div>
  );
};
